/**
 * Mediabunny-based video encoder
 * Uses WebCodecs with hardware acceleration for fast, high-quality MP4 export
 */

import {
    Output,
    Mp4OutputFormat,
    WebMOutputFormat,
    BufferTarget,
    CanvasSource,
    AudioBufferSource,
    type VideoEncodingConfig,
    type AudioEncodingConfig,
} from 'mediabunny'
import { ExportSettings, ExportProgress } from './types'
import { createRenderContext, renderFrame, disposeRenderContext } from './frame-renderer'
import { useEditorStore } from '@/store/editor-store'
import { useRecordingStore } from '@/store/recording-store'

/**
 * Decode audio from a video/audio blob using Web Audio API.
 * Returns an AudioBuffer or null if no audio is present.
 */
async function decodeAudioFromBlob(blob: Blob): Promise<AudioBuffer | null> {
    try {
        const arrayBuffer = await blob.arrayBuffer()
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
        try {
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
            await audioContext.close()
            return audioBuffer
        } catch {
            // No audio track or unsupported format
            await audioContext.close()
            return null
        }
    } catch {
        return null
    }
}

/**
 * Mix the main recording audio with additional audio layers (music, etc.)
 * using OfflineAudioContext so all sources are rendered into one buffer.
 */
async function mixAudioWithLayers(
    mainBuffer: AudioBuffer | null,
    layers: { offset: number; duration: number; trimStart?: number; volume: number; muted: boolean; fadeIn?: number; fadeOut?: number; pan?: number; url?: string; type: string }[],
    duration: number,
    recordingVolume = 1,
    recordingMuted = false,
    trackVisibility: { video: boolean; zoom: boolean; text: boolean; camera: boolean; music: boolean; sfx: boolean } = { video: true, zoom: true, text: true, camera: true, music: true, sfx: true }
): Promise<AudioBuffer | null> {
    const activeLayers = layers.filter(l => {
        if (l.muted || !l.url) return false
        if (l.type === 'music' && !trackVisibility.music) return false
        if (l.type === 'sfx' && !trackVisibility.sfx) return false
        return true
    })
    const isMainVideoMuted = !trackVisibility.video
    if (!mainBuffer && activeLayers.length === 0) return null

    const sampleRate = mainBuffer?.sampleRate ?? 44100
    const numChannels = 2 // Always stereo output for pan support
    const frameCount = Math.ceil(duration * sampleRate)

    const offlineCtx = new OfflineAudioContext(numChannels, frameCount, sampleRate)

    if (mainBuffer && !recordingMuted && !isMainVideoMuted) {
        const src = offlineCtx.createBufferSource()
        src.buffer = mainBuffer
        const gain = offlineCtx.createGain()
        gain.gain.value = recordingVolume
        src.connect(gain)
        gain.connect(offlineCtx.destination)
        src.start(0)
    }

    for (const layer of activeLayers) {
        try {
            const resp = await fetch(layer.url!)
            const arrayBuffer = await resp.arrayBuffer()
            const decoded = await offlineCtx.decodeAudioData(arrayBuffer)
            const src = offlineCtx.createBufferSource()
            src.buffer = decoded

            const gain = offlineCtx.createGain()
            gain.gain.value = layer.volume

            // Fade in
            const fadeIn = layer.fadeIn ?? 0
            if (fadeIn > 0) {
                gain.gain.setValueAtTime(0, layer.offset)
                gain.gain.linearRampToValueAtTime(layer.volume, layer.offset + fadeIn)
            }
            // Fade out
            const fadeOut = layer.fadeOut ?? 0
            const layerEnd = layer.offset + layer.duration
            if (fadeOut > 0 && layerEnd <= duration) {
                gain.gain.setValueAtTime(layer.volume, layerEnd - fadeOut)
                gain.gain.linearRampToValueAtTime(0, layerEnd)
            }

            // Stereo pan
            const panValue = layer.pan ?? 0
            if (panValue !== 0) {
                const panner = offlineCtx.createStereoPanner()
                panner.pan.value = panValue
                src.connect(gain)
                gain.connect(panner)
                panner.connect(offlineCtx.destination)
            } else {
                src.connect(gain)
                gain.connect(offlineCtx.destination)
            }

            src.start(Math.min(layer.offset, duration), layer.trimStart ?? 0, layer.duration)
        } catch {
            // Skip undecodable layer
        }
    }

    return offlineCtx.startRendering()
}

/**
 * Trim or pad an AudioBuffer to match a target duration.
 * If the audio is longer, it trims. If shorter, it pads with silence.
 */
function adjustAudioDuration(audioBuffer: AudioBuffer, targetDuration: number): AudioBuffer {
    const targetLength = Math.ceil(targetDuration * audioBuffer.sampleRate)
    const audioContext = new OfflineAudioContext(
        audioBuffer.numberOfChannels,
        targetLength,
        audioBuffer.sampleRate
    )

    // Create a new buffer of the right length
    const newBuffer = audioContext.createBuffer(
        audioBuffer.numberOfChannels,
        targetLength,
        audioBuffer.sampleRate
    )

    // Copy audio data (truncated or zero-padded)
    for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
        const sourceData = audioBuffer.getChannelData(ch)
        const destData = newBuffer.getChannelData(ch)
        const copyLength = Math.min(sourceData.length, targetLength)
        destData.set(sourceData.subarray(0, copyLength))
    }

    return newBuffer
}

/**
 * Check if WebCodecs is supported
 */
export function isWebCodecsSupported(): boolean {
    return 'VideoEncoder' in window && 'VideoDecoder' in window && 'VideoFrame' in window
}

/**
 * Extract frames from video blob as generator
 */
/**
 * Extract synchronized frames from main video and optionally camera video
 */
async function* extractTimelineFrames(
    videoBlob: Blob,
    cameraBlob: Blob | null,
    frameRate: number,
    duration: number,
    clips: any[], // Using any[] to avoid complex import issues if Clip type is tricky, but it matches the store
    onProgress?: (decoded: number, total: number) => void
): AsyncGenerator<{
    mainCanvas: OffscreenCanvas | null;
    cameraCanvas: OffscreenCanvas | null;
    timestamp: number;
    duration: number
}, void, unknown> {
    const videoUrl = URL.createObjectURL(videoBlob)
    const video = document.createElement('video')
    video.src = videoUrl
    video.muted = true
    video.preload = 'auto'

    let cameraVideo: HTMLVideoElement | null = null
    let cameraUrl: string | null = null
    if (cameraBlob) {
        cameraUrl = URL.createObjectURL(cameraBlob)
        cameraVideo = document.createElement('video')
        cameraVideo.src = cameraUrl
        cameraVideo.muted = true
        cameraVideo.preload = 'auto'
    }

    await Promise.all([
        new Promise<void>((resolve, reject) => {
            video.onloadedmetadata = () => resolve()
            video.onerror = () => reject(new Error('Failed to load main video'))
        }),
        cameraVideo ? new Promise<void>((resolve, reject) => {
            cameraVideo!.onloadedmetadata = () => resolve()
            cameraVideo!.onerror = () => reject(new Error('Failed to load camera video'))
        }) : Promise.resolve()
    ])

    const totalFrames = Math.max(1, Math.ceil(duration * frameRate))
    const frameDuration = 1 / frameRate

    // Create canvases for drawing frames
    const mainCanvas = new OffscreenCanvas(video.videoWidth, video.videoHeight)
    const mainCtx = mainCanvas.getContext('2d', { alpha: false })!

    let cameraCanvas: OffscreenCanvas | null = null
    let cameraCtx: OffscreenCanvasRenderingContext2D | null = null
    if (cameraVideo) {
        cameraCanvas = new OffscreenCanvas(cameraVideo.videoWidth, cameraVideo.videoHeight)
        cameraCtx = cameraCanvas.getContext('2d', { alpha: false })!
    }

    const sortedClips = [...clips].filter(c => c.status === 'visible').sort((a, b) => a.offset - b.offset);

    for (let i = 0; i < totalFrames; i++) {
        const timelineTime = i * frameDuration

        // Find active clip for main video (speed-aware)
        const activeClip = sortedClips.find(c => {
            const spd = c.speed ?? 1
            return timelineTime >= c.offset && timelineTime < c.offset + (c.end - c.start) / spd
        })

        let mainFrameReady = false
        if (activeClip) {
            const spd = activeClip.speed ?? 1
            const sourceTime = activeClip.start + (timelineTime - activeClip.offset) * spd
            if (sourceTime <= video.duration) {
                video.currentTime = sourceTime
                await new Promise<void>((resolve) => {
                    video.onseeked = () => resolve()
                })
                mainCtx.drawImage(video, 0, 0)
                mainFrameReady = true
            }
        }

        let cameraFrameReady = false
        if (cameraVideo && timelineTime <= cameraVideo.duration) {
            cameraVideo.currentTime = timelineTime
            await new Promise<void>((resolve) => {
                cameraVideo!.onseeked = () => resolve()
            })
            cameraCtx!.drawImage(cameraVideo, 0, 0)
            cameraFrameReady = true
        }

        onProgress?.(i + 1, totalFrames)

        yield {
            mainCanvas: mainFrameReady ? mainCanvas : null,
            cameraCanvas: cameraFrameReady ? cameraCanvas : null,
            timestamp: timelineTime,
            duration: frameDuration
        }
    }

    URL.revokeObjectURL(videoUrl)
    if (cameraUrl) URL.revokeObjectURL(cameraUrl)
}

/**
 * Main export function using Mediabunny
 */
export async function exportVideo(
    settings: ExportSettings,
    onProgress: (progress: ExportProgress) => void
): Promise<Blob> {
    if (!isWebCodecsSupported()) {
        throw new Error('WebCodecs is not supported in this browser')
    }

    const editorState = useEditorStore.getState()
    const recordingState = useRecordingStore.getState()
    const videoBlob = recordingState.blob
    const cameraBlob = recordingState.cameraBlob

    if (!videoBlob) {
        throw new Error('No video to export')
    }

    // Initialize progress
    onProgress({
        stage: 'initializing',
        progress: 0,
        currentFrame: 0,
        totalFrames: 0,
        estimatedTimeRemaining: 0,
        message: 'Initializing Mediabunny encoder...',
    })

    const { width, height } = settings.resolution
    const { frameRate, videoBitrate, format } = settings

    // Get video dimensions from source
    const tempVideo = document.createElement('video')
    tempVideo.src = URL.createObjectURL(videoBlob)
    tempVideo.muted = true
    await new Promise<void>((resolve) => {
        tempVideo.onloadedmetadata = () => resolve()
    })

    // If duration is Infinity (common in browser recordings), try to fix it by seeking to the end
    if (tempVideo.duration === Infinity) {
        tempVideo.currentTime = 1e101 // seek to an impossible end
        await new Promise<void>((resolve) => {
            const onSeeked = () => {
                tempVideo.removeEventListener('seeked', onSeeked)
                tempVideo.currentTime = 0
                resolve()
            }
            tempVideo.addEventListener('seeked', onSeeked)
        })
    }

    const sourceWidth = tempVideo.videoWidth
    const sourceHeight = tempVideo.videoHeight

    // PICK THE BEST DURATION:
    // We calculate the "Real" duration based on the max end time of any visible content 
    // (clips or text layers) to avoid exporting blank canvas at the end.
    const clipsEnd = editorState.clips
        .filter(c => c.status === 'visible')
        .reduce((max, c) => Math.max(max, c.offset + (c.end - c.start) / (c.speed ?? 1)), 0)

    const textsEnd = editorState.textLayers
        .reduce((max, t) => Math.max(max, t.endTime), 0)

    // Calculate content-based duration
    const contentDuration = Math.max(clipsEnd, textsEnd)

    // Final duration: content-based, with fallback to store value or video metadata
    const duration = contentDuration > 0
        ? contentDuration
        : (editorState.duration || recordingState.duration || tempVideo.duration)

    URL.revokeObjectURL(tempVideo.src)

    // Safety check for duration
    if (!Number.isFinite(duration) || duration <= 0) {
        throw new Error(`Invalid video duration (${duration}). Please ensure the recording is loaded correctly.`)
    }

    const totalFrames = Math.max(1, Math.ceil(duration * frameRate))

    // Pre-load background image if needed
    let bgImage: ImageBitmap | null = null
    const { backgroundType, backgroundValue } = editorState
    if (backgroundType === 'image' || backgroundValue.startsWith('http') || backgroundValue.startsWith('blob:') || backgroundValue.startsWith('/')) {
        try {
            const response = await fetch(backgroundValue)
            const blob = await response.blob()
            bgImage = await createImageBitmap(blob)
        } catch (e) {
            console.error("Failed to pre-load background image for export:", e)
        }
    }

    // Create the render canvas for effects
    const renderCanvas = new OffscreenCanvas(width, height)
    const renderCtx = renderCanvas.getContext('2d', { alpha: false })!

    // Create render context for applying effects
    const effectContext = createRenderContext(width, height, sourceWidth, sourceHeight, bgImage)

    // Create Mediabunny output with chosen format
    const outputFormat = format === 'webm'
        ? new WebMOutputFormat()
        : new Mp4OutputFormat({ fastStart: 'in-memory' })

    const output = new Output({
        format: outputFormat,
        target: new BufferTarget(),
    })

    // Create video encoding config
    const videoEncodingConfig: VideoEncodingConfig = {
        codec: format === 'webm' ? 'vp9' : 'avc', // VP9 for WebM, H.264 for MP4
        bitrate: videoBitrate,
    }

    // Create canvas source for video track
    const videoSource = new CanvasSource(renderCanvas, videoEncodingConfig)
    output.addVideoTrack(videoSource, {
        frameRate: frameRate,
    })

    // --- AUDIO TRACK ---
    // Decode recording audio, then mix with any additional audio layers (music etc.)
    let audioSource: AudioBufferSource | null = null
    let audioBuffer: AudioBuffer | null = null
    if (settings.includeAudio) {
        const recordingBuffer = videoBlob ? await decodeAudioFromBlob(videoBlob) : null
        const trimmedRecording = recordingBuffer ? adjustAudioDuration(recordingBuffer, duration) : null
        const { audioLayers, recordingVolume, recordingMuted, trackVisibility } = useEditorStore.getState()
        audioBuffer = await mixAudioWithLayers(trimmedRecording, audioLayers, duration, recordingVolume, recordingMuted, trackVisibility)
        if (audioBuffer) {
            const audioEncodingConfig: AudioEncodingConfig = {
                codec: format === 'webm' ? 'opus' : 'aac',
                bitrate: settings.audioBitrate,
            }
            audioSource = new AudioBufferSource(audioEncodingConfig)
            output.addAudioTrack(audioSource)
        }
    }

    // Start the output
    await output.start()

    // Feed audio data right after starting (it's independent of frame rendering)
    if (audioSource && audioBuffer) {
        await audioSource.add(audioBuffer)
        audioSource.close()
    }

    // Track timing for ETA (using a simple moving average for stability)
    const startTime = performance.now()
    const frameTimes: number[] = []
    const WINDOW_SIZE = 10
    let framesProcessed = 0

    onProgress({
        stage: 'rendering',
        progress: 5,
        currentFrame: 0,
        totalFrames,
        estimatedTimeRemaining: 0,
        message: 'Rendering frames...',
    })

    // Process frames
    for await (const { mainCanvas, cameraCanvas, timestamp } of extractTimelineFrames(videoBlob, cameraBlob, frameRate, duration, editorState.clips)) {
        // Create VideoFrames for the renderer
        const videoFrame = mainCanvas ? new VideoFrame(mainCanvas, {
            timestamp: timestamp * 1_000_000,
        }) : null

        const cameraFrame = cameraCanvas ? new VideoFrame(cameraCanvas, {
            timestamp: timestamp * 1_000_000,
        }) : null

        // Apply effects using frame renderer
        const renderedBitmap = renderFrame(effectContext, videoFrame, timestamp, editorState, cameraFrame)

        // Draw rendered bitmap to the export canvas
        renderCtx.drawImage(renderedBitmap, 0, 0)

        // Add frame to Mediabunny
        const frameDuration = 1 / frameRate
        await videoSource.add(timestamp, frameDuration)

        // Cleanup
        videoFrame?.close()
        cameraFrame?.close()
        renderedBitmap.close()

        framesProcessed++

        // Update progress
        const now = performance.now()
        const elapsedSinceStart = (now - startTime) / 1000

        // Calculate smoothed FPS using moving window
        frameTimes.push(now)
        if (frameTimes.length > WINDOW_SIZE) frameTimes.shift()

        let estimatedTimeRemaining = 0
        if (frameTimes.length > 1) {
            const windowElapsed = (frameTimes[frameTimes.length - 1] - frameTimes[0]) / 1000
            const currentFps = (frameTimes.length - 1) / windowElapsed
            const remaining = (totalFrames - framesProcessed) / currentFps
            estimatedTimeRemaining = Number.isFinite(remaining) ? Math.ceil(remaining) : 0
        } else {
            const globalFps = framesProcessed / elapsedSinceStart
            const remaining = (totalFrames - framesProcessed) / globalFps
            estimatedTimeRemaining = Number.isFinite(remaining) ? Math.ceil(remaining) : 0
        }

        const progressValue = 5 + (framesProcessed / totalFrames) * 85 // 5-90%

        onProgress({
            stage: 'rendering',
            progress: progressValue,
            currentFrame: framesProcessed,
            totalFrames,
            estimatedTimeRemaining,
            message: `Rendering frame ${framesProcessed} / ${totalFrames}`,
        })
    }

    // Close the video source
    videoSource.close()

    // Finalize output
    onProgress({
        stage: 'finalizing',
        progress: 90,
        currentFrame: totalFrames,
        totalFrames,
        estimatedTimeRemaining: 5,
        message: 'Finalizing video...',
    })

    await output.finalize()

    // Get the final buffer
    const buffer = (output.target as BufferTarget).buffer!

    // Cleanup
    disposeRenderContext(effectContext)

    onProgress({
        stage: 'complete',
        progress: 100,
        currentFrame: totalFrames,
        totalFrames,
        estimatedTimeRemaining: 0,
        message: 'Export complete!',
    })

    return new Blob([buffer], { type: format === 'webm' ? 'video/webm' : 'video/mp4' })
}

/**
 * Encode an AudioBuffer as a 16-bit PCM WAV Blob
 */
function encodeWav(audioBuffer: AudioBuffer): Blob {
    const numChannels = audioBuffer.numberOfChannels
    const sampleRate = audioBuffer.sampleRate
    const numSamples = audioBuffer.length
    const bytesPerSample = 2 // 16-bit
    const dataBytes = numSamples * numChannels * bytesPerSample
    const buffer = new ArrayBuffer(44 + dataBytes)
    const view = new DataView(buffer)

    const write = (offset: number, str: string) => {
        for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
    }
    write(0, 'RIFF')
    view.setUint32(4, 36 + dataBytes, true)
    write(8, 'WAVE')
    write(12, 'fmt ')
    view.setUint32(16, 16, true)              // chunk size
    view.setUint16(20, 1, true)               // PCM format
    view.setUint16(22, numChannels, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, sampleRate * numChannels * bytesPerSample, true) // byte rate
    view.setUint16(32, numChannels * bytesPerSample, true)              // block align
    view.setUint16(34, 16, true)              // bits per sample
    write(36, 'data')
    view.setUint32(40, dataBytes, true)

    // Interleave channel data as int16
    let offset = 44
    const channels = Array.from({ length: numChannels }, (_, ch) => audioBuffer.getChannelData(ch))
    for (let i = 0; i < numSamples; i++) {
        for (let ch = 0; ch < numChannels; ch++) {
            const s = Math.max(-1, Math.min(1, channels[ch][i]))
            view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true)
            offset += 2
        }
    }

    return new Blob([buffer], { type: 'audio/wav' })
}

/**
 * Extract and export the audio track from the recording as a WAV file.
 * Returns null if the recording has no audio.
 */
export async function exportAudio(
    onProgress?: (msg: string) => void
): Promise<Blob | null> {
    const blob = useRecordingStore.getState().blob
    if (!blob) throw new Error('No recording to export')

    onProgress?.('Decoding audio…')
    const audioBuffer = await decodeAudioFromBlob(blob)
    if (!audioBuffer) return null

    const duration = useEditorStore.getState().duration
    const trimmed = duration > 0 ? adjustAudioDuration(audioBuffer, duration) : audioBuffer

    onProgress?.('Encoding WAV…')
    return encodeWav(trimmed)
}

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()

    // Give the browser time to handle the click before cleaning up
    setTimeout(() => {
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }, 100)
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

/**
 * Estimate output file size
 */
export function estimateFileSize(settings: ExportSettings, durationSeconds: number): number {
    const videoBits = settings.videoBitrate * durationSeconds
    const audioBits = settings.includeAudio ? settings.audioBitrate * durationSeconds : 0
    const totalBits = videoBits + audioBits
    const overhead = 1.05 // 5% container overhead
    return Math.ceil((totalBits / 8) * overhead)
}
